//These are the things we want to use to define the core colors and measurments we use in the theme.
//They should be used in the theme definitions, but not on components. It's more behind the scenes.
const BASE_FONT_SIZE = 14;
const colors = {
    FOUNDATION_100: '#d1f3ff',
    FOUNDATION_200: '#a9d5e5',
    FOUNDATION_300: '#8cb4c2',
    FOUNDATION_400: '#6b8d99',
    FOUNDATION_500: '#283b42',
    ACCENT_50: '#fedfd7',
    ACCENT_100: '#e5927d',
    ACCENT_200: '#c37c6a',
    ACCENT_300: '#84564a',
    CONTRAST_LIGHTEST: '#ffffff',
    CONTRAST_DARKEST: '#000000',
    TEXT_HIGH_EMPHASIS: '#203D48',
    TEXT_MEDIUM_EMPHASIS: '#717F96',
    TEXT_LOW_EMPHASIS: '#B0CCD6',
    SUCCESS_300: '#CCFFDA',
    SUCCESS_500: '#42A45C',
    WARNING_300: '#FFE9CC',
    WARNING_500: '#F5A73F',
    ALERT_300: '#FFCCCC',
    ALERT_500: '#E50000',
    PROGRESS_300: '#E0E8FF',
    PROGRESS_500: '#4169E1',
    STRUCTURAL_50: '#FCFEFF',
    STRUCTURAL_100: '#F7FDFF',
    STRUCTURAL_200: '#F0FBFF',
    STRUCTURAL_300: '#F2F8FA',
    STRUCTURAL_400: '#F6FDFF',
    CHIPS_GREY_50: '#F0F2F4',
    CHIPS_GREY_500: '#45556E',
    CHIPS_BROWN_50: '#FEEEEA',
    CHIPS_GREEN_50: '#E0FCF0',
    CHIPS_GREEN_500: '#26835B',
    CHIPS_YELLOW_50: '#FFF7D8',
    CHIPS_YELLOW_500: '#E1B129',
    CHIPS_RED_50: '#FFEDEC',
    CHIPS_RED_500: '#C83729',
    CHIPS_BLUE_50: '#EAF2FF',
    CHIPS_BLUE_500: '#186AE1',
    CHIPS_TEAL_50: '#EDF4F7',
    CHIPS_TEAL_500: '#45626E',
    CHIPS_PLUM_50: '#F3EAF2',
    CHIPS_PLUM_500: '#875882',
    OPACITY_SECONDARY_10: '#D1F3FF1A',
    OPACITY_PRIMARY_15: '#FEDFD7',
    // Queue Priority Order (CDMX-14856) — parity with legacy Sass in
    // design-tokens/legacy/scss/biome/_biome-colors.scss ($status-queue-*)
    STATUS_QUEUE_HEALTHY: '#5ba37a',
    STATUS_QUEUE_AT_RISK: '#f6844e',
    STATUS_QUEUE_CRITICAL: '#f24646',
    STATUS_QUEUE_NEEDS_ATTENTION: '#f6844e',
    STATUS_QUEUE_SUPERSTAR: '#bb529e' // $tropical_500
};
const pxToRem = (px, baseFontSize = BASE_FONT_SIZE)=>{
    return `${px / baseFontSize}rem`;
};

const colorTheme = {
    foundation: {
        100: colors.FOUNDATION_100,
        200: colors.FOUNDATION_200,
        300: colors.FOUNDATION_300,
        400: colors.FOUNDATION_400,
        500: colors.FOUNDATION_500
    },
    accent: {
        50: colors.ACCENT_50,
        100: colors.ACCENT_100,
        200: colors.ACCENT_200,
        300: colors.ACCENT_300
    },
    contrast: {
        lightest: colors.CONTRAST_LIGHTEST,
        darkest: colors.CONTRAST_DARKEST
    },
    text: {
        highEmphasis: colors.TEXT_HIGH_EMPHASIS,
        mediumEmphasis: colors.TEXT_MEDIUM_EMPHASIS,
        lowEmphasis: colors.TEXT_LOW_EMPHASIS,
        light: colors.CONTRAST_LIGHTEST,
        interactive: colors.ACCENT_200
    },
    stroke: {
        100: colors.FOUNDATION_100,
        200: colors.FOUNDATION_200,
        300: colors.FOUNDATION_400,
        400: colors.FOUNDATION_500
    },
    success: {
        300: colors.SUCCESS_300,
        500: colors.SUCCESS_500
    },
    warning: {
        300: colors.WARNING_300,
        500: colors.WARNING_500
    },
    alert: {
        300: colors.ALERT_300,
        500: colors.ALERT_500
    },
    progress: {
        300: colors.PROGRESS_300,
        500: colors.PROGRESS_500
    },
    structural: {
        0: colors.CONTRAST_LIGHTEST,
        50: colors.STRUCTURAL_50,
        100: colors.STRUCTURAL_100,
        200: colors.STRUCTURAL_200,
        300: colors.STRUCTURAL_300,
        400: colors.STRUCTURAL_400
    },
    chips: {
        grey: {
            50: colors.CHIPS_GREY_50,
            500: colors.CHIPS_GREY_500
        },
        brown: {
            50: colors.CHIPS_BROWN_50,
            500: colors.ACCENT_200
        },
        green: {
            50: colors.CHIPS_GREEN_50,
            500: colors.CHIPS_GREEN_500
        },
        yellow: {
            50: colors.CHIPS_YELLOW_50,
            500: colors.CHIPS_YELLOW_500
        },
        red: {
            50: colors.CHIPS_RED_50,
            500: colors.CHIPS_RED_500
        },
        blue: {
            50: colors.CHIPS_BLUE_50,
            500: colors.CHIPS_BLUE_500
        },
        teal: {
            50: colors.CHIPS_TEAL_50,
            500: colors.CHIPS_TEAL_500
        },
        plum: {
            50: colors.CHIPS_PLUM_50,
            500: colors.CHIPS_PLUM_500
        }
    },
    opacity: {
        secondary: colors.OPACITY_SECONDARY_10,
        primary: colors.OPACITY_PRIMARY_15
    },
    // Queue Priority Order (CDMX-14856). Chakra adapter for queue UI; production
    // styling today uses legacy Sass ($status-queue-* in dashboard-ui). Migrate
    // components to theme.colors.* when the app stack catches up.
    statusQueue: {
        healthy: colors.STATUS_QUEUE_HEALTHY,
        atRisk: colors.STATUS_QUEUE_AT_RISK,
        critical: colors.STATUS_QUEUE_CRITICAL,
        needsAttention: colors.STATUS_QUEUE_NEEDS_ATTENTION,
        superstar: colors.STATUS_QUEUE_SUPERSTAR
    },
    dragHandle: colors.FOUNDATION_300,
    priorityBadge: {
        background: colors.CHIPS_GREY_50,
        text: colors.CHIPS_GREY_500
    },
    coderAvatar: {
        assigned: colors.FOUNDATION_400,
        available: colors.TEXT_LOW_EMPHASIS
    },
    coderAction: {
        removeBorder: colors.FOUNDATION_300,
        addBorder: colors.FOUNDATION_400
    }
};

const spacingTheme = {
    1: pxToRem(1),
    2: pxToRem(2),
    4: pxToRem(4),
    8: pxToRem(8),
    10: pxToRem(10),
    12: pxToRem(12),
    16: pxToRem(16),
    20: pxToRem(20),
    24: pxToRem(24),
    28: pxToRem(28),
    32: pxToRem(32),
    36: pxToRem(36),
    40: pxToRem(40),
    44: pxToRem(44),
    48: pxToRem(48),
    52: pxToRem(52),
    56: pxToRem(56),
    60: pxToRem(60),
    76: pxToRem(76),
    80: pxToRem(80),
    92: pxToRem(92),
    96: pxToRem(96),
    100: pxToRem(100),
    120: pxToRem(120),
    132: pxToRem(132),
    152: pxToRem(152),
    240: pxToRem(240),
    300: pxToRem(300),
    320: pxToRem(320),
    400: pxToRem(400)
};

const radiiTheme = {
    3: pxToRem(3),
    6: pxToRem(6),
    18: pxToRem(18),
    50: pxToRem(50)
};

const bordersTheme = {
    '1px': `${pxToRem(1)} solid`,
    '2px': `${pxToRem(2)} solid`,
    '3px': `${pxToRem(3)} solid`,
    '8px': `${pxToRem(8)} solid`
};

const textStyleTheme = {
    fontFamily: [
        'Roboto',
        'normal'
    ].join(','),
    h1: {
        fontSize: pxToRem(29.03),
        fontWeight: '600',
        lineHeight: pxToRem(34),
        letterSpacing: pxToRem(0)
    },
    h2: {
        fontSize: pxToRem(24.19),
        fontWeight: '600',
        lineHeight: pxToRem(28),
        letterSpacing: pxToRem(0)
    },
    h3: {
        fontSize: pxToRem(20.16),
        fontWeight: '600',
        lineHeight: pxToRem(24),
        letterSpacing: pxToRem(0)
    },
    h4: {
        fontSize: pxToRem(16.8),
        fontWeight: '600',
        lineHeight: pxToRem(20),
        letterSpacing: pxToRem(0)
    },
    'Subtitle 1': {
        fontSize: pxToRem(14),
        fontWeight: '700',
        lineHeight: pxToRem(16),
        letterSpacing: pxToRem(0)
    },
    'Subtitle 2': {
        fontSize: pxToRem(11.67),
        fontWeight: '700',
        lineHeight: pxToRem(14),
        letterSpacing: pxToRem(0)
    },
    'Body 1': {
        fontSize: pxToRem(14),
        fontWeight: '400',
        lineHeight: pxToRem(16),
        letterSpacing: pxToRem(0)
    },
    'Body 2': {
        fontSize: pxToRem(11.67),
        fontWeight: '400',
        lineHeight: pxToRem(14),
        letterSpacing: pxToRem(0)
    },
    'Caption 1': {
        fontSize: pxToRem(11.67),
        fontWeight: '700',
        lineHeight: pxToRem(14),
        letterSpacing: pxToRem(0)
    },
    'Caption 2': {
        fontSize: pxToRem(11.67),
        fontWeight: '400',
        lineHeight: pxToRem(14),
        letterSpacing: pxToRem(0)
    },
    'Overline 1': {
        fontSize: pxToRem(9.72),
        fontWeight: '600',
        lineHeight: pxToRem(12),
        letterSpacing: pxToRem(0)
    },
    'Overline 2': {
        fontSize: pxToRem(9.72),
        fontWeight: '400',
        lineHeight: pxToRem(12),
        letterSpacing: pxToRem(0)
    }
};

const primaryButton = {
    bg: colorTheme.accent[100],
    color: colorTheme.text['light'],
    fontSize: pxToRem(14),
    fontWeight: '500',
    lineHeight: pxToRem(16),
    borderRadius: pxToRem(3),
    h: pxToRem(40),
    pt: pxToRem(12),
    pr: pxToRem(16),
    pl: pxToRem(16),
    pb: pxToRem(12),
    _hover: {
        bg: colorTheme.accent[200]
    },
    _disabled: {
        bg: colorTheme.accent[50]
    },
    _active: {
        bg: colorTheme.accent[300]
    },
    _focus: {
        boxShadow: 'none'
    },
    _focusVisble: {
        outline: '2px solid rgba(66, 153, 225, 0.6) !important',
        boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6) !important'
    }
};
const secondaryButton = {
    color: colorTheme.accent[100],
    border: `${pxToRem(1)} solid`,
    borderColor: colorTheme.accent[100],
    fontSize: pxToRem(14),
    fontWeight: '500',
    lineHeight: pxToRem(16),
    borderRadius: pxToRem(3),
    h: pxToRem(40),
    pt: pxToRem(12),
    pr: pxToRem(16),
    pl: pxToRem(16),
    pb: pxToRem(12),
    _hover: {
        bg: colorTheme.accent[50],
        color: colorTheme.accent[100],
        borderColor: colorTheme.accent[100]
    },
    _disabled: {
        color: colorTheme.accent[50],
        borderColor: colorTheme.accent[50]
    },
    _active: {
        bg: colorTheme.accent[50],
        color: colorTheme.accent[200],
        borderColor: colorTheme.accent[200]
    },
    _focus: {
        boxShadow: 'none'
    },
    _focusVisible: {
        outline: '2px solid rgba(66, 153, 225, 0.6) !important',
        boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6) !important'
    }
};
const linksButton = {
    color: colorTheme.accent[100],
    borders: 'none',
    fontSize: pxToRem(14),
    fontWeight: '500',
    lineHeight: pxToRem(16),
    borderRadius: pxToRem(3),
    h: pxToRem(40),
    pt: pxToRem(12),
    pr: pxToRem(16),
    pl: pxToRem(16),
    pb: pxToRem(12),
    _hover: {
        bg: colorTheme.accent[50]
    },
    _disabled: {
        color: colorTheme.accent[50]
    },
    _active: {
        color: colorTheme.accent[200]
    },
    _focus: {
        boxShadow: 'none'
    }
};
const panelToggleButton = {
    bg: colorTheme.foundation[400],
    color: colorTheme.text['light'],
    fontSize: pxToRem(14),
    fontWeight: '500',
    lineHeight: pxToRem(16),
    borderRadius: pxToRem(100),
    h: pxToRem(24),
    w: pxToRem(24),
    minW: pxToRem(24),
    minH: pxToRem(24),
    pt: pxToRem(0),
    pr: pxToRem(0),
    pl: pxToRem(0),
    pb: pxToRem(0),
    _hover: {
        bg: colorTheme.accent[300]
    },
    _disabled: {
        bg: colorTheme.foundation[100]
    },
    _active: {
        bg: colorTheme.foundation[400]
    },
    _focus: {
        boxShadow: 'none'
    },
    _focusVisible: {
        outline: '2px solid rgba(66, 153, 225, 0.6) !important',
        boxShadow: '0 0 0 3px rgba(66, 153, 225, 0.6) !important'
    }
};
const clockToggleButton = {
    bg: 'none',
    color: colorTheme.text['high emphasis'],
    fontSize: pxToRem(9.72),
    fontWeight: '400',
    lineHeight: pxToRem(16),
    borderRadius: 'none',
    h: '100%',
    w: '100%',
    minW: pxToRem(20),
    minH: pxToRem(20),
    pt: pxToRem(0),
    pr: pxToRem(4),
    pl: pxToRem(4),
    pb: pxToRem(0),
    gap: spacingTheme[4],
    _hover: {
        bg: colorTheme.accent[50]
    },
    _disabled: {
        bg: 'none'
    },
    _active: {
        bg: colorTheme.accent[50]
    },
    _focus: {
        boxShadow: 'outline'
    }
};
const clockToggleButtonv1 = {
    bg: colorTheme.chips.teal[50],
    color: colorTheme.text['high emphasis'],
    border: `${pxToRem(1)} solid`,
    borderColor: colorTheme.chips.teal[50],
    fontSize: pxToRem(9.72),
    fontWeight: '400',
    lineHeight: pxToRem(16),
    borderRadius: radiiTheme[3],
    h: spacingTheme[20],
    minW: pxToRem(20),
    minH: pxToRem(22),
    pt: pxToRem(1),
    pr: pxToRem(4),
    pl: pxToRem(4),
    pb: pxToRem(1),
    gap: spacingTheme[4],
    _hover: {
        bg: colorTheme.accent[50],
        borderColor: colorTheme.accent[50]
    },
    _disabled: {
        bg: 'none'
    },
    _active: {
        bg: colorTheme.accent[50]
    },
    _focus: {
        boxShadow: 'outline'
    }
};
const buttonTheme = {
    variants: {
        primary: primaryButton,
        secondary: secondaryButton,
        links: linksButton,
        toggle: panelToggleButton,
        clock: clockToggleButton,
        clockv1: clockToggleButtonv1
    }
};

const popperContent = {
    'z-Index': '55'
};
const popoverContent = {
    borderRadius: radiiTheme[3],
    boxShadow: 'l5',
    borderColor: colorTheme.foundation[300],
    '--popper-arrow-shadow-color': colorTheme.foundation[300]
};
const popoverBody = {
    px: spacingTheme[20],
    py: spacingTheme[20]
};
const popoverVariantTime = {
    content: {
        w: pxToRem(250)
    }
};
const popoverTheme = {
    baseStyle: {
        popper: popperContent,
        content: popoverContent,
        body: popoverBody
    },
    variants: {
        time: popoverVariantTime
    }
};

const shadowColor = '40, 59, 66';
const shadowUmbraOpacity = '.14';
const shadowPenumbraOpacity = '.12';
const shadowAmbiantOpacity = '.20';
const shadowUmbra = `rgba(${shadowColor}, ${shadowUmbraOpacity})`;
const shadowPenumbra = `rgba(${shadowColor}, ${shadowPenumbraOpacity})`;
const shadowAmbiant = `rgba(${shadowColor}, ${shadowAmbiantOpacity})`;
const shadowTheme = {
    /* Did not define styles for xs or sm */ l1: `0 3px 3px -2px ${shadowAmbiant}, 0 3px 4px 0 ${shadowUmbra}, 0 1px 8px 0 ${shadowPenumbra}`,
    l2: `0 3px 5px -1px ${shadowAmbiant}, 0 6px 10px 0 ${shadowUmbra}, 0 1px 18px 0 ${shadowPenumbra}`,
    l3: `0 5px 6px -3px ${shadowAmbiant}, 0 9px 12px 1px ${shadowUmbra}, 0 3px 16px 2px ${shadowPenumbra}`,
    l4: `0 6px 7px -4px ${shadowAmbiant}, 0 11px 15px 1px ${shadowUmbra}, 0 4px 20px 3px ${shadowPenumbra}`,
    l5: `0 8px 9px -5px ${shadowAmbiant}, 0 15px 22px 2px ${shadowUmbra}, 0 6px 28px 5px ${shadowPenumbra}`
};

const textInput = {
    height: pxToRem(40),
    field: {
        border: `${pxToRem(1)} solid`,
        borderColor: colorTheme.stroke[200],
        borderRadius: pxToRem(3),
        color: colorTheme.text['highEmphasis'],
        focusBorderColor: colorTheme.stroke[400],
        fontSize: pxToRem(14),
        pl: pxToRem(12),
        _placeholder: {
            color: colorTheme.text['lowEmphasis']
        },
        _hover: {
            borderColor: colorTheme.stroke[300]
        },
        _active: {
            borderColor: colorTheme.stroke[200],
            color: colorTheme.text['highEmphasis']
        },
        _focus: {
            borderColor: colorTheme.stroke[400]
        },
        _invalid: {
            borderColor: colorTheme.alert[500]
        },
        _disabled: {
            bg: colorTheme.structural[300],
            borderColor: colorTheme.stroke[200],
            color: colorTheme.text['highEmphasis']
        },
        _info: {
            borderColor: colorTheme.stroke[400]
        },
        _autofill: {
            transition: 'background-color 5000s ease-in-out 0s'
        }
    },
    element: {
        color: colorTheme.stroke[300]
    },
    addon: {
        border: `${pxToRem(1)} solid`,
        borderColor: colorTheme.structural[300],
        bg: colorTheme.structural[300],
        color: colorTheme.structural[300],
        pt: pxToRem(6),
        pb: pxToRem(6),
        pl: pxToRem(10),
        pr: pxToRem(10)
    }
};
const searchInput = {
    height: pxToRem(40),
    field: {
        border: `${pxToRem(1)} solid`,
        borderColor: colorTheme.stroke[200],
        borderRadius: pxToRem(3),
        color: colorTheme.text['highEmphasis'],
        focusBorderColor: colorTheme.stroke[400],
        _placeholder: {
            color: colorTheme.text['lowEmphasis']
        },
        _hover: {
            borderColor: colorTheme.stroke[300]
        },
        _active: {
            borderColor: colorTheme.stroke[200],
            color: colorTheme.text['highEmphasis']
        },
        _focus: {
            borderColor: colorTheme.stroke[400]
        },
        _invalid: {
            borderColor: colorTheme.alert[500]
        },
        _disabled: {
            bg: colorTheme.structural[300],
            borderColor: colorTheme.stroke[200],
            color: colorTheme.text['highEmphasis']
        },
        _info: {
            borderColor: colorTheme.stroke[400]
        }
    },
    element: {
        color: colorTheme.foundation[300],
        pl: pxToRem(12),
        pr: pxToRem(12),
        pt: pxToRem(16),
        pb: pxToRem(16)
    },
    addon: {
        border: `${pxToRem(1)} solid`,
        borderColor: colorTheme.structural[300],
        bg: colorTheme.structural[300],
        color: colorTheme.stroke[300],
        pt: pxToRem(6),
        pb: pxToRem(6),
        pl: pxToRem(10),
        pr: pxToRem(10)
    }
};
const formStyles = {
    parts: [
        'control',
        'container',
        'requiredIndicator',
        'helperText',
        'errorMessage'
    ],
    baseStyle: {
        control: {
            height: pxToRem(60)
        },
        container: {
            label: {
                color: colorTheme.text['highEmphasis'],
                fontSize: pxToRem(11.67)
            }
        },
        requiredIndicator: {
            color: colorTheme.success[500],
            fontSize: pxToRem(11.67)
        },
        helperText: {
            mt: pxToRem(8),
            color: colorTheme.text['mediumEmphasis'],
            fontSize: pxToRem(11.67)
        },
        errorMessage: {
            color: colorTheme.alert[500],
            fontSize: pxToRem(11.67)
        }
    }
};
const inputTheme = {
    variants: {
        text: textInput,
        search: searchInput
    }
};
const checkboxSizes = {
    md: {
        control: {
            height: pxToRem(12),
            width: pxToRem(12)
        },
        label: {
            marginLeft: pxToRem(8)
        }
    }
};
const checkboxTheme = {
    sizes: checkboxSizes
};

const theme = {
    colors: colorTheme,
    space: spacingTheme,
    radii: radiiTheme,
    shadows: shadowTheme,
    borders: bordersTheme,
    textStyles: textStyleTheme,
    components: {
        Button: buttonTheme,
        Input: inputTheme,
        Form: formStyles,
        Checkbox: checkboxTheme,
        Popover: popoverTheme
    },
    styles: {
        global: {
            '.cmx-form-input, .cmx-form-select': {
                appearance: 'none',
                background: colorTheme.contrast.lightest,
                backgroundImage: 'none',
                border: `1px solid ${colorTheme.stroke[200]}`,
                borderRadius: '3px',
                color: colorTheme.text.highEmphasis,
                display: 'block',
                fontSize: '14px',
                height: '1.8rem',
                lineHeight: '1.2rem',
                maxWidth: '100%',
                outline: 'none',
                padding: '0.25rem 0.4rem',
                position: 'relative',
                transition: 'background .2s,border .2s,box-shadow .2s,color .2s',
                width: '100%'
            },
            'textarea.cmx-form-input': {
                height: 'auto'
            },
            '.cmx-form-input:focus, .cmx-form-select:focus': {
                borderColor: colorTheme.stroke[400],
                boxShadow: `0 0 0 0.1rem ${colorTheme.opacity.secondary}`
            },
            '.cmx-form-checkbox': {
                display: 'inline-block'
            }
        }
    }
};

export { colorTheme, pxToRem, theme };
//# sourceMappingURL=biome.esm.js.map
